import {
  Print,
  type DmDetectedPrinters,
  type DmPrinterAddJobResponse,
  type DmPrinterStatus,
  type DMSupportedPrinter
} from '@nsftx/systems-sdk'
import { logService } from '@nsftx/seven-client-retail-utils'
import DmPrintError from '#src/classes/DmPrintError'
import deviceManagementService from './deviceManagementService'
import { useDeviceManagementPrintStore } from '#src/stores/deviceManagementPrintStore'
import errorParser from '#src/common/error-handling/errorParser'
import { getResolvedPrinter } from './printUtilsService'
import { getSavedPrinter, savePrinter } from './printerStorageService'
import { useDeviceManagementPrinterStatusStore } from '#src/stores/deviceManagementPrinterStatusStore'
import { parsePrinterStatusResponse } from './printerStatusService'
import * as deviceManagementPrintApiService from './deviceManagementPrintApiService'
import type { DmPrintConfig } from '#src/interfaces/DmPrintConfig'
import type { DmPrintAddJobOptions } from '#src/interfaces/PrintAddJobOptions'
import { toRaw } from 'vue'
import AcceptorsHooksManager from './acceptorsHooksManager'
import type HookErrorResponse from '#src/types/HookErrorResponse'

const LOG_PREFIX = '[deviceManagementPrintService]'
const PRINT_READY_EVENT_TIMEOUT_DELAY_SEC = 10

let print: Print
let isPrintReady = false
let areAcceptorsDisabledOnPaperLow = true
let printReadyTimeout: number | undefined

/**
 * @throws {DmPrintError}
 */
const init = async (config: DmPrintConfig) => {
  areAcceptorsDisabledOnPaperLow = config.areAcceptorsDisabledOnPaperLow ?? true
  registerHooks()
  await initPrintModule()
  registerPrintEvents()

  const detectedPrinters =
    (await deviceManagementPrintApiService.getDetectedPrinters(getPrint())) || []

  const supportedPrinters =
    (await deviceManagementPrintApiService.getSupportedPrinters(getPrint())) || []

  if (!detectedPrinters.length) {
    throw new DmPrintError(
      'Cannot set printer data because there is no detected printers',
      'DM_PRINT_NO_DETECTED_PRINTERS'
    )
  }

  handlePrinterData(config, detectedPrinters, supportedPrinters)

  await getPrinterStatus()
}

const validatePaperLowOnStartAcceptors = (params: { errorResponses: Array<HookErrorResponse> }) => {
  const { isStatusPaperLow } = useDeviceManagementPrinterStatusStore()

  if (isStatusPaperLow()) {
    params.errorResponses.push({
      id: 'acceptors-start-error-paper-low',
      message: 'Acceptors could not be started. Paper is low.',
      code: 'DM_BEFORE_ACCEPTORS_START_PAPER_LOW'
    })
  }
}

const validatePaperOutOnStartAcceptors = (params: { errorResponses: Array<HookErrorResponse> }) => {
  const { isStatusPaperOut } = useDeviceManagementPrinterStatusStore()

  if (isStatusPaperOut()) {
    params.errorResponses.push({
      id: 'acceptors-start-error-paper-out',
      message: 'Acceptors could not be started. Paper is out.',
      code: 'DM_BEFORE_ACCEPTORS_START_PAPER_OUT'
    })
  }
}

const validatePrinterSelectedStateOnStartAcceptors = (params: {
  errorResponses: Array<HookErrorResponse>
}) => {
  const { selectedPrinter } = useDeviceManagementPrintStore()

  if (!selectedPrinter) {
    params.errorResponses.push({
      id: 'acceptors-start-error-printer-not-selected',
      message: 'Acceptors could not be started. Printer is not selected.',
      code: 'DM_BEFORE_ACCEPTORS_START_PRINTER_NOT_SELECTED'
    })
  }
}

const validatePrinterOfflineStateOnStartAcceptors = (params: {
  errorResponses: Array<HookErrorResponse>
}) => {
  const { isStatusOffline } = useDeviceManagementPrinterStatusStore()

  if (isStatusOffline()) {
    params.errorResponses.push({
      id: 'acceptors-start-error-printer-offline',
      message: 'Acceptors could not be started. Printer is offline.',
      code: 'DM_BEFORE_ACCEPTORS_START_PRINTER_OFFLINE'
    })
  }
}

const registerHooks = () => {
  AcceptorsHooksManager.hook('BeforeAcceptorsStart', (params) => {
    if (areAcceptorsDisabledOnPaperLow) {
      validatePaperLowOnStartAcceptors(params)
    }

    validatePaperOutOnStartAcceptors(params)
    validatePrinterSelectedStateOnStartAcceptors(params)
    validatePrinterOfflineStateOnStartAcceptors(params)
  })
}

const handlePrinterData = (
  { persist }: DmPrintConfig,
  detectedPrinters: DmDetectedPrinters,
  supportedPrinters: DMSupportedPrinter[]
) => {
  const { setDetectedPrinters, setPrinter, setSupportedPrinters } = useDeviceManagementPrintStore()

  setSupportedPrinters(supportedPrinters)
  logService.debug(`${LOG_PREFIX} Setting supported printers`, {
    code: 'DM_PRINT_SET_SUPPORTED_PRINTERS'
  })

  setDetectedPrinters(detectedPrinters)
  logService.debug(`${LOG_PREFIX} Setting detected printers`, {
    code: 'DM_PRINT_SET_DETECTED_PRINTERS',
    printers: detectedPrinters
  })

  const savedPrinter = persist ? getSavedPrinter(persist) : null
  const resolvedPrinter = getResolvedPrinter(savedPrinter)
  logService.info(`${LOG_PREFIX} Setting active printer`, {
    code: 'DM_PRINT_SET_ACTIVE_PRINTER',
    printer: toRaw(resolvedPrinter)
  })

  if (!resolvedPrinter) {
    throw new DmPrintError('Failed to resolve active printer', 'DM_PRINT_NO_ACTIVE_PRINTER')
  }

  setPrinter(resolvedPrinter)

  if (persist) {
    savePrinter(resolvedPrinter, persist)
  }
}

const registerPrintEvents = () => {
  const print = getPrint()

  print.on('statusChange', onPrinterStatusChange)
}

const onPrinterStatusChange = (printerStatusResponse: DmPrinterStatus) => {
  const { setPrinterStatus } = useDeviceManagementPrinterStatusStore()
  const printerStatus = parsePrinterStatusResponse(printerStatusResponse)

  if (!printerStatus.valid) {
    logService.error(`${LOG_PREFIX} Printer status change - error detected`, {
      code: 'DM_PRINT_STATUS_CHANGE_ERROR',
      upstream_message: printerStatus.message || 'Printer status is invalid',
      details: {
        printerStatus
      }
    })
  } else {
    logService.debug(`${LOG_PREFIX} Printer status change - warning detected`, {
      code: 'DM_PRINT_STATUS_CHANGE_WARNING',
      details: {
        printerStatus
      }
    })
  }

  logService.debug(`${LOG_PREFIX} Setting printer status`, {
    code: 'DM_PRINT_SET_PRINTER_STATUS',
    details: {
      printerStatus,
      context: 'printerStatusChange'
    }
  })
  setPrinterStatus(printerStatus)
}

const initPrintModule = () =>
  new Promise<void>((resolve, reject) => {
    print = new Print(deviceManagementService.getCore(), false)
    print.on('ready', () => onPrintReady(resolve))

    printReadyTimeout = window.setTimeout(() => {
      reject(
        new DmPrintError(
          `Failed to resolve print ready event after ${PRINT_READY_EVENT_TIMEOUT_DELAY_SEC} seconds`,
          'DM_PRINT_INIT_ERROR'
        )
      )
    }, PRINT_READY_EVENT_TIMEOUT_DELAY_SEC * 1000)
  })

const onPrintReady = (resolve: () => void) => {
  logService.info(`${LOG_PREFIX} Print module successfully initialized.`, {
    code: 'DM_PRINT_INIT_SUCCESS'
  })
  isPrintReady = true
  clearTimeout(printReadyTimeout)

  resolve()
}

/**
 * @throws {DmPrintError}
 */
const getSelectedPrinter = () => {
  const { selectedPrinter } = useDeviceManagementPrintStore()
  if (!selectedPrinter) {
    throw new DmPrintError('printer_not_selected', 'DM_PRINT_ADD_JOB_ERROR')
  }

  return selectedPrinter
}

/**
 * @throws {DmPrintError}
 */
const getPrint = () => {
  if (!isPrintReady) {
    throw new DmPrintError('Print module is not initialized', 'DM_PRINT_NOT_INITIALIZED')
  }

  return print
}

/**
 * @throws {DmPrintError}
 */
const getPrinterStatus = async () => {
  const printerStatusResponse = await deviceManagementPrintApiService.getPrinterStatus(
    getPrint(),
    getSelectedPrinter()
  )
  const printerStatus = parsePrinterStatusResponse(printerStatusResponse)
  const { setPrinterStatus } = useDeviceManagementPrinterStatusStore()

  logService.debug(`${LOG_PREFIX} Setting printer status`, {
    code: 'DM_PRINT_SET_PRINTER_STATUS',
    details: {
      printerStatus,
      context: 'getPrinterStatus'
    }
  })
  setPrinterStatus(printerStatus)

  if (!printerStatus.valid) {
    logService.error(`${LOG_PREFIX} Printer status check failed`, {
      upstream_message: printerStatus.message || 'Printer status is invalid',
      code: 'DM_PRINT_STATUS_ERROR',
      details: {
        printerStatus
      }
    })
    throw new DmPrintError(printerStatus.message, 'DM_PRINT_STATUS_ERROR', {
      context: { printerStatus }
    })
  }

  return printerStatus
}

/**
 * @throws {DmPrintError}
 */
const addPrintJob = async (template: string, options: DmPrintAddJobOptions) => {
  const { meta } = options
  const { productDisplayId = '', requestId = '' } = meta
  const requestUuid = requestId

  logService.info(`${LOG_PREFIX} Sending template to add print job`, {
    code: 'DM_PRINT_SEND_TO_ADD_JOB',
    request_id: requestUuid,
    product_displayid: productDisplayId
  })

  try {
    const response = await deviceManagementPrintApiService.addPrintJob(
      getPrint(),
      getSelectedPrinter(),
      template
    )
    if (!isAddPrintJobSuccess(response)) {
      throw new DmPrintError(response, 'DM_PRINT_ADD_JOB_ERROR')
    }

    handleAddPrintJobSuccess(requestUuid, productDisplayId)

    return response
  } catch (error) {
    handleAddPrintJobError(error, requestUuid, productDisplayId, options)
  }
}

const isAddPrintJobSuccess = (
  response: DmPrinterAddJobResponse
): response is { success: boolean } => typeof response === 'object' && response.success

const handleAddPrintJobSuccess = async (requestUuid: string, productDisplayId: string) => {
  logService.info(`${LOG_PREFIX} Successfully printed`, {
    request_id: requestUuid,
    product_displayid: productDisplayId,
    code: 'DM_PRINT_SUCCESS'
  })
}

/**
 * @throws {DmPrintError}
 */
const handleAddPrintJobError = (
  error: unknown,
  requestUuid: string,
  productId: string,
  debugData: object
) => {
  const { upstream_message, upstream_code } = errorParser.parseUpstream(error)
  const errorMessage =
    upstream_message ?? (upstream_code === '429' && 'printer_queue_limit_reached')

  logService.error(`${LOG_PREFIX} Add print job has failed`, {
    ...errorParser.parseUpstream(error),
    code: 'DM_PRINT_ERROR',
    request_id: requestUuid,
    product_displayid: productId,
    details: {
      ...debugData
    }
  })

  throw new DmPrintError(errorMessage, 'DM_PRINT_ADD_JOB_ERROR', { cause: error })
}

const refreshDetectedPrinters = async () => {
  const { setDetectedPrinters } = useDeviceManagementPrintStore()

  const detectedPrinters =
    (await deviceManagementPrintApiService.getDetectedPrinters(getPrint())) || []

  logService.debug(`${LOG_PREFIX} Refreshing detected printers`, {
    code: 'DM_PRINT_REFRESH_DETECTED_PRINTERS',
    printers: detectedPrinters
  })

  setDetectedPrinters(detectedPrinters)
}

export default {
  init,
  getPrinterStatus,
  addPrintJob,
  refreshDetectedPrinters
}
